import { cn } from "@/lib/utils";
import { createContext, useContext, useState, ComponentChildren, HTMLAttributes } from "preact/compat";

// Tabs Context
interface TabsContextType {
  value: string;
  setValue: (value: string) => void;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs components must be used within a <Tabs />");
  }
  return context;
};

// Tabs
interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
}

export function Tabs({ children, value: controlledValue, onValueChange, defaultValue, className, ...props }: TabsProps) {
  const [value, setValue] = useState(controlledValue || defaultValue || "");

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ value, setValue: handleValueChange }}>
      <div className={cn("w-full", className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// TabsList
interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
}

export function TabsList({ children, className, ...props }: TabsListProps) {
  return (
    <div
      className={cn(
        "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// TabsTrigger
interface TabsTriggerProps extends HTMLAttributes<HTMLButtonElement> {
  children: ComponentChildren;
  value: string;
}

export function TabsTrigger({ children, value, className, ...props }: TabsTriggerProps) {
  const { value: currentValue, setValue } = useTabsContext();
  const isActive = currentValue === value;

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        isActive
          ? "bg-background text-foreground shadow-sm"
          : "hover:bg-background/50",
        className
      )}
      onClick={() => setValue(value)}
      {...props}
    >
      {children}
    </button>
  );
}

// TabsContent
interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ComponentChildren;
  value: string;
}

export function TabsContent({ children, value, className, ...props }: TabsContentProps) {
  const { value: currentValue } = useTabsContext();
  
  if (currentValue !== value) {
    return null;
  }

  return (
    <div
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}